/**
 * ⚠️⚠️ OTRA FUENTE DEL MISMO MEDIO YA TIENE OPERACIONES EN ESTAS FECHAS.
 *
 * MEDIDO POR LA UI el 2026-09-14 (e2e, hl-qa2): la cartera HyperLiquid ya
 * estaba importada por API; el mismo usuario subió por «Añadir CSV» el export
 * de LA MISMA CUENTA, la previa dijo «✓ 11 · ⚠ 2» sin un aviso y el neto de
 * margen −9,597276 USDC quedó contado dos veces. La API y el CSV no se pueden
 * emparejar fila a fila (comisiones aparte frente a `closedPnl` con comisión,
 * marcas de tiempo a milisegundos), así que la detección de duplicados no lo ve.
 *
 * El backend devuelve el HECHO comprobable —filas vivas del mismo medio con
 * otro `import_source` dentro del rango— y este aviso lo pone delante:
 *   · `before-import` (previa de «Añadir CSV»): con casilla de confirmación
 *     obligatoria; el modal no deja importar hasta marcarla.
 *   · `after-import` (aviso post-sincronización e historial): sin casilla, con
 *     la consecuencia en pasado y dónde revisarlo.
 *
 * La copia vive en `transactions.json` (`importSourceOverlap.*`): habla de
 * filas del libro por fuente de importación, y el catálogo `imports.json` está
 * en su techo de tamaño.
 */

import type { ImportSourceOverlap } from '@bat/shared/contracts/jobs/import-job.dto'
import { useTranslation } from 'react-i18next'

import { userConfigService } from '@/shared/services/user-config.service'

export interface ImportSourceOverlapNoticeProps {
  overlap: ImportSourceOverlap
  variant: 'before-import' | 'after-import'
  /** `before-import` only: whether the user ticked the acknowledgement. */
  acknowledged?: boolean
  /** `before-import` only: renders the acknowledgement checkbox when set. */
  onAcknowledgedChange?: (acknowledged: boolean) => void
  testId?: string
}

export const ImportSourceOverlapNotice = ({
  overlap,
  variant,
  acknowledged = false,
  onAcknowledgedChange,
  testId = 'import-source-overlap-notice',
}: ImportSourceOverlapNoticeProps) => {
  const { t } = useTranslation('transactions')
  const checkboxId = `${testId}-acknowledge`

  return (
    <div
      data-testid={testId}
      role="alert"
      className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-200 [html[data-theme=light]_&]:text-amber-800 space-y-1.5"
    >
      <p className="font-semibold text-sm" data-testid={`${testId}-title`}>
        ⚠ {t('importSourceOverlap.title', { platform: overlap.platformDisplayName })}
      </p>
      <ul className="space-y-0.5">
        {overlap.sources.map((source) => (
          <li
            key={source.importSource}
            data-testid={`${testId}-source`}
            data-import-source={source.importSource}
          >
            {t('importSourceOverlap.sourceLine', {
              count: source.count,
              formatted: userConfigService.formatNumberWithFixedDecimals(source.count, 0),
              platform: overlap.platformDisplayName,
              source: t(`importSourceOverlap.channels.${source.channel}`, {
                label: source.importSource,
              }),
              from: userConfigService.formatDate(source.firstDate),
              to: userConfigService.formatDate(source.lastDate),
            })}
          </li>
        ))}
      </ul>
      <p data-testid={`${testId}-consequence`}>
        {variant === 'before-import'
          ? t('importSourceOverlap.consequenceBeforeImport')
          : t('importSourceOverlap.consequenceAfterImport')}
      </p>
      {variant === 'before-import' && onAcknowledgedChange && (
        <label htmlFor={checkboxId} className="flex items-start gap-2 pt-1 cursor-pointer">
          <input
            id={checkboxId}
            type="checkbox"
            data-testid={`${testId}-acknowledge-checkbox`}
            checked={acknowledged}
            onChange={(event) => onAcknowledgedChange(event.target.checked)}
            className="mt-0.5 rounded border-crypto-border bg-crypto-bg text-crypto-accent focus:ring-crypto-accent"
          />
          <span className="font-medium">{t('importSourceOverlap.acknowledge')}</span>
        </label>
      )}
    </div>
  )
}
